import React, { createContext, useContext, useReducer } from "react";
import { SnackbarProvider, useSnackbar } from "notistack";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NOTIFICATION_CONFIG } from "../utils/constants";

// Estado inicial del contexto de notificaciones
const initialState = {
  notifications: [],
  globalSettings: {
    maxSnackbars: NOTIFICATION_CONFIG.MAX_SNACKBARS,
    dense: false,
    preventDuplicate: false,
    anchorOrigin: NOTIFICATION_CONFIG.ANCHOR_ORIGIN,
  },
};

// Tipos de acciones
const NotificationActionTypes = {
  ADD_NOTIFICATION: "ADD_NOTIFICATION",
  REMOVE_NOTIFICATION: "REMOVE_NOTIFICATION",
  CLEAR_ALL: "CLEAR_ALL",
  UPDATE_SETTINGS: "UPDATE_SETTINGS",
};

// Reducer para manejo del estado de notificaciones
const notificationReducer = (state, action) => {
  switch (action.type) {
    case NotificationActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload.notification],
      };

    case NotificationActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter((notification) => notification.id !== action.payload.id),
      };

    case NotificationActionTypes.CLEAR_ALL:
      return {
        ...state,
        notifications: [],
      };

    case NotificationActionTypes.UPDATE_SETTINGS:
      return {
        ...state,
        globalSettings: { ...state.globalSettings, ...action.payload.settings },
      };

    default:
      return state;
  }
};

// Crear el contexto
const NotificationContext = createContext();

// Exportar el contexto como named export
export { NotificationContext };

// Hook para usar el contexto de notificaciones
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification debe ser usado dentro de un NotificationProvider");
  }
  return context;
};

// Componente interno que maneja las notificaciones
const NotificationManager = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Generar ID único para notificaciones
  const generateId = () => {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Función para mostrar notificación de éxito
  const showSuccess = (message, options = {}) => {
    const id = generateId();
    const notification = {
      id,
      type: "success",
      message,
      timestamp: Date.now(),
      ...options,
    };

    dispatch({
      type: NotificationActionTypes.ADD_NOTIFICATION,
      payload: { notification },
    });

    const snackbarId = enqueueSnackbar(message, {
      variant: "success",
      autoHideDuration: options.duration || NOTIFICATION_CONFIG.SUCCESS_DURATION,
      action: (key) => (
        <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });

    return { id, snackbarId };
  };

  // Función para mostrar notificación de error
  const showError = (message, options = {}) => {
    const id = generateId();
    const notification = {
      id,
      type: "error",
      message,
      timestamp: Date.now(),
      ...options,
    };

    dispatch({
      type: NotificationActionTypes.ADD_NOTIFICATION,
      payload: { notification },
    });

    const snackbarId = enqueueSnackbar(message, {
      variant: "error",
      autoHideDuration: options.duration || NOTIFICATION_CONFIG.ERROR_DURATION,
      action: (key) => (
        <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });

    return { id, snackbarId };
  };

  // Función para mostrar notificación de advertencia
  const showWarning = (message, options = {}) => {
    const id = generateId();
    const notification = {
      id,
      type: "warning",
      message,
      timestamp: Date.now(),
      ...options,
    };

    dispatch({
      type: NotificationActionTypes.ADD_NOTIFICATION,
      payload: { notification },
    });

    const snackbarId = enqueueSnackbar(message, {
      variant: "warning",
      autoHideDuration: options.duration || NOTIFICATION_CONFIG.DEFAULT_DURATION,
      action: (key) => (
        <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });

    return { id, snackbarId };
  };

  // Función para mostrar notificación informativa
  const showInfo = (message, options = {}) => {
    const id = generateId();
    const notification = {
      id,
      type: "info",
      message,
      timestamp: Date.now(),
      ...options,
    };

    dispatch({
      type: NotificationActionTypes.ADD_NOTIFICATION,
      payload: { notification },
    });

    const snackbarId = enqueueSnackbar(message, {
      variant: "info",
      autoHideDuration: options.duration || NOTIFICATION_CONFIG.DEFAULT_DURATION,
      action: (key) => (
        <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
      ...options,
    });

    return { id, snackbarId };
  };

  // Función genérica para mostrar notificación
  const show = (message, variant = "default", options = {}) => {
    switch (variant) {
      case "success":
        return showSuccess(message, options);
      case "error":
        return showError(message, options);
      case "warning":
        return showWarning(message, options);
      case "info":
        return showInfo(message, options);
      default:
        return showInfo(message, options);
    }
  };

  // Función para remover notificación específica
  const remove = (id) => {
    dispatch({
      type: NotificationActionTypes.REMOVE_NOTIFICATION,
      payload: { id },
    });
  };

  // Función para limpiar todas las notificaciones
  const clearAll = () => {
    dispatch({ type: NotificationActionTypes.CLEAR_ALL });
  };

  // Función para actualizar configuración global
  const updateSettings = (settings) => {
    dispatch({
      type: NotificationActionTypes.UPDATE_SETTINGS,
      payload: { settings },
    });
  };

  // Funciones de conveniencia para casos comunes
  const showLoginSuccess = (userName) => {
    return showSuccess(`¡Bienvenido, ${userName}!`);
  };

  const showLoginError = () => {
    return showError("Error de autenticación. Verifica tus credenciales.");
  };

  const showLogoutSuccess = () => {
    return showInfo("Sesión cerrada correctamente.");
  };

  const showSaveSuccess = (itemName = "elemento") => {
    return showSuccess(`${itemName} guardado correctamente.`);
  };

  const showDeleteSuccess = (itemName = "elemento") => {
    return showSuccess(`${itemName} eliminado correctamente.`);
  };

  const showUploadSuccess = (fileName) => {
    return showSuccess(`Archivo "${fileName}" subido correctamente.`);
  };

  const showUploadError = (fileName, error) => {
    return showError(`Error al subir "${fileName}": ${error}`);
  };

  const showNetworkError = () => {
    return showError("Error de conexión. Verifica tu conexión a internet.");
  };

  const showPermissionError = () => {
    return showError("No tienes permisos para realizar esta acción.");
  };

  // Valor del contexto
  const contextValue = {
    // Estado
    ...state,

    // Funciones principales
    show,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    remove,
    clearAll,
    updateSettings,

    // Funciones de conveniencia
    showLoginSuccess,
    showLoginError,
    showLogoutSuccess,
    showSaveSuccess,
    showDeleteSuccess,
    showUploadSuccess,
    showUploadError,
    showNetworkError,
    showPermissionError,
  };

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
};

// Provider principal que incluye SnackbarProvider
export const NotificationProvider = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnackbars={NOTIFICATION_CONFIG.MAX_SNACKBARS}
      dense={false}
      anchorOrigin={NOTIFICATION_CONFIG.ANCHOR_ORIGIN}
      preventDuplicate={false}>
      <NotificationManager>{children}</NotificationManager>
    </SnackbarProvider>
  );
};

export default NotificationContext;
